import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatDate, getAllOrders } from '../lib/orderService';
import { getAllEquipment } from '../lib/equipmentService';
import { getAllClients } from '../lib/clientService';
import { getAllSpecs } from '../lib/specService';
import { invalidateCache, isCacheStale } from '../lib/cacheService';
import BrandBadge from './BrandBadge';
import { Building, User, Phone, Mail, Calendar, FileText, Package, Eye, EyeOff, Edit2, Save, X, RefreshCw } from 'lucide-react';

const OrderReport = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [client, setClient] = useState(null);
  const [brands, setBrands] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRefreshSuggestion, setShowRefreshSuggestion] = useState(false);
  
  // Temporary editing states (don't persist)
  const [tempClientData, setTempClientData] = useState(null);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [hiddenPhotos, setHiddenPhotos] = useState(new Set());

  useEffect(() => {
    fetchOrderData();
  }, [id]);

  const fetchOrderData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError('');
      setShowRefreshSuggestion(false);

      // If force refresh requested, invalidate cache first to ensure fresh data
      if (forceRefresh) {
        console.log('Force refresh requested - invalidating cache...');
        invalidateCache('orders');
        invalidateCache('clients');
        invalidateCache('equipment');
        invalidateCache('specs');
      }

      // For reports, use the standard local-first approach - the services will automatically 
      // refresh if cache is stale according to the 30-minute cache expiry policy
      console.log('Loading data for report generation...');
      
      // Check if orders cache is stale (more than 5 minutes old) to suggest refresh for recent changes like photo comments
      const ordersStale = isCacheStale('orders', 5); // 5 minutes threshold for reports
      
      const [allOrders, allClients, allEquipment, allSpecs] = await Promise.all([
        getAllOrders(forceRefresh), // Use cache-first unless forcing refresh
        getAllClients(forceRefresh), // Use cache-first unless forcing refresh
        getAllEquipment(forceRefresh), // Use cache-first unless forcing refresh
        getAllSpecs(forceRefresh) // Use cache-first unless forcing refresh
      ]);

      // Show refresh suggestion if orders cache was stale (for photo comments and other recent changes)
      if (ordersStale && !forceRefresh) {
        setShowRefreshSuggestion(true);
      }
      
      // Find the specific order
      const orderData = allOrders.find(order => order.id === id);
      
      if (!orderData) {
        setError('Orden no encontrada');
        return;
      }

      setOrder(orderData);
      setSpecs(allSpecs);

      // Process client information if order has a client ID
      if (orderData.cliente) {
        try {
          const clientData = allClients.find(client => client.id === orderData.cliente);
          if (clientData) {
            setClient(clientData);
            setTempClientData(clientData); // Initialize temp data
          }
        } catch (error) {
          console.error('Error processing client data:', error);
        }
      }

      // Process equipment information
      if (orderData.equipos && orderData.equipos.length > 0) {
        try {
          const equipmentResults = orderData.equipos.map((equipo) => {
            if (equipo.catalogId) {
              // Find equipment in data
              const catalogData = allEquipment.find(item => item.id === equipo.catalogId);
              if (catalogData) {
                return {
                  ...equipo,
                  catalogData,
                  type: 'catalog'
                };
              }
            }
            return {
              ...equipo,
              type: 'exempt'
            };
          });
          
          setEquipment(equipmentResults);
        } catch (error) {
          console.error('Error processing equipment:', error);
        }
      }

    } catch (error) {
      console.error('Error fetching order data:', error);
      setError('Error al cargar la información de la orden');
    } finally {
      setLoading(false);
    }
  };

  const handleClientEdit = () => {
    setIsEditingClient(true);
  };

  const handleClientSave = () => {
    setIsEditingClient(false);
  };

  const handleClientCancel = () => {
    setTempClientData(client); // Reset to original data
    setIsEditingClient(false);
  };

  const handleClientDataChange = (field, value) => {
    setTempClientData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactDataChange = (contactIndex, field, value) => {
    setTempClientData(prev => ({
      ...prev,
      contactos: prev.contactos.map((contact, index) => 
        index === contactIndex ? { ...contact, [field]: value } : contact
      )
    }));
  };

  const togglePhotoVisibility = (equipmentIndex, photoUrl) => {
    const photoKey = `${equipmentIndex}-${photoUrl}`;
    setHiddenPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoKey)) {
        newSet.delete(photoKey);
      } else {
        newSet.add(photoKey);
      }
      return newSet;
    });
  };

  const isPhotoVisible = (equipmentIndex, photoUrl) => {
    const photoKey = `${equipmentIndex}-${photoUrl}`;
    return !hiddenPhotos.has(photoKey);
  };

  const getPrincipalContact = (contactos) => {
    if (!contactos || contactos.length === 0) return null;
    return contactos.find(c => c.esPrincipal) || contactos[0];
  };

  const getClientDisplayName = (clientData) => {
    if (!clientData) return 'Sin cliente';
    const principal = getPrincipalContact(clientData.contactos);
    return clientData.nombreEmpresa || principal?.nombre || 'Cliente';
  };

  // Helper function to merge catalog and custom specifications
  const getMergedSpecifications = (equipo) => {
    if (!equipo.catalogData || !equipo.catalogData.especificaciones) {
      return {};
    }
    
    const catalogSpecs = equipo.catalogData.especificaciones;
    const customSpecs = equipo.customSpecs || {};
    
    // Create merged specifications - custom values override catalog defaults
    const mergedSpecs = { ...catalogSpecs };
    
    // Override with custom values where they exist and are not empty
    Object.keys(customSpecs).forEach(specName => {
      if (customSpecs[specName] && customSpecs[specName].trim() !== '') {
        mergedSpecs[specName] = customSpecs[specName];
      }
    });
    
    return mergedSpecs;
  };

  // Helper function to check if a spec value is custom (different from catalog default)
  const isCustomSpec = (equipo, specName, specValue) => {
    if (!equipo.catalogData || !equipo.catalogData.especificaciones) return false;
    
    const catalogValue = equipo.catalogData.especificaciones[specName];
    const customSpecs = equipo.customSpecs || {};
    
    // Check if this spec has a custom value that's different from catalog
    return customSpecs[specName] && 
           customSpecs[specName].trim() !== '' && 
           customSpecs[specName] !== catalogValue;
  };

  // Helper function to get specifications ordered by priority
  const getOrderedSpecifications = (especificaciones) => {
    if (!especificaciones || Object.keys(especificaciones).length === 0) return [];
    
    // Create a map of spec names to their priority for quick lookup
    const specPriorityMap = {};
    specs.forEach(spec => {
      specPriorityMap[spec.nombre] = spec.prioridad || 999; // Default high priority for specs without priority
    });
    
    // Convert equipment specs to array and sort by priority
    const equipmentSpecs = Object.entries(especificaciones);
    
    return equipmentSpecs.sort((a, b) => {
      const priorityA = specPriorityMap[a[0]] || 999;
      const priorityB = specPriorityMap[b[0]] || 999;
      return priorityA - priorityB; // Lower priority number = higher priority (displayed first)
    });
  };

  // Helper function to determine if photos should use compact layout
  const shouldUseCompactLayout = (equipo, equipmentIndex, validEquipment) => {
    // Get filtered photos (excluding webm files)
    const filteredPhotos = equipo.photos ? equipo.photos.filter(photo => 
      !photo.downloadURL.toLowerCase().includes('.webm')
    ) : [];
    
    if (filteredPhotos.length === 0) return false;
    
    // Check if any photos have comments
    const hasPhotosWithComments = filteredPhotos.some(photo => photo.comment && photo.comment.trim() !== '');
    
    if (!hasPhotosWithComments) return false;
    
    // Use compact layout for first equipment with commented photos, or when there are many photos with comments
    const photosWithComments = filteredPhotos.filter(photo => photo.comment && photo.comment.trim() !== '').length;
    
    return (equipmentIndex === 0) || (photosWithComments >= 2) || (filteredPhotos.length >= 4 && hasPhotosWithComments);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="text-center py-8 text-gray-500">
          <div className="animate-pulse">
            Cargando reporte...
          </div>
          <div className="text-sm mt-2 text-gray-400">
            Preparando información de la orden
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          
          /* Report container */
          .report-container { 
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Equipment grid layout */
          .equipment-grid {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 1rem !important;
          }

          /* Multi-column specifications layout for long lists */
          .specs-multi-column {
            column-count: 2 !important;
            column-gap: 1rem !important;
          }

          .specs-multi-column li {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          
          /* Photo grid layout - conditional optimization for print */
          .photo-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
          
          /* Compact photo layout when needed to prevent page breaks */
          .photos-section.compact-layout .photo-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.75rem !important;
          }
          
          .photos-section.compact-layout .photo-item img {
            height: 120px !important;
          }
          
          .photos-section.compact-layout .photo-item .p-2 {
            padding: 0.375rem !important;
          }
          
          .photos-section.compact-layout .photo-item .text-xs {
            font-size: 0.625rem !important;
            line-height: 0.875rem !important;
          }
          
          /* Section headers - avoid breaking from their content */
          .section-header {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          
          /* Keep sections together when possible, but be more flexible */
          .section-content {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Equipment sections - more flexible breaking */
          .equipment-item {
            page-break-inside: auto !important;
            break-inside: auto !important;
            margin-bottom: 1rem !important;
          }
          
          /* Equipment basic info - keep together */
          .equipment-basic-info {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Photo sections within equipment - allow breaking but prefer avoiding */
          .photos-section {
            page-break-before: avoid !important;
            page-break-inside: auto !important;
            break-before: avoid !important;
            break-inside: auto !important;
          }
          
          /* Photos header - keep with at least one photo */
          .photos-header {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          
          /* Individual photos - keep comments with images */
          .photo-item {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Major sections - allow page break before if needed */
          .major-section {
            page-break-before: auto !important;
            break-before: auto !important;
          }
          
          /* Force page break for very large sections */
          .force-page-break {
            page-break-before: always !important;
            break-before: always !important;
          }
          
          /* Orphan and widow control */
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid !important;
            break-after: avoid !important;
            orphans: 2 !important;
            widows: 2 !important;
          }
          
          /* Avoid breaking small content blocks */
          .info-block {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Warranty section specific rules */
          .warranty-section {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Large warranty section - allow breaking but prefer not to */
          .warranty-section.large-content {
            page-break-inside: auto !important;
            break-inside: auto !important;
            orphans: 2 !important;
            widows: 2 !important;
          }
        }
        @page {
          margin: 0.5in 1in 1in 1in;
          size: letter;
        }
        .report-container {
          width: 8.5in;
          min-height: 11in;
          background: white;
          position: relative;
        }
        
        /* Watermark styling */
        .watermark-overlay {
          display: none;
        }
        
        @media print {
          .watermark-overlay {
            display: block !important;
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            width: 550px !important;
            height: 440px !important;
            opacity: 0.08 !important;
            z-index: 1 !important;
            pointer-events: none !important;
          }
          
          /* Ensure content backgrounds allow watermark to show through */
          .bg-gray-50 {
            background-color: rgba(249, 250, 251, 0.8) !important;
          }
        }
      `}</style>

      {/* Report Content */}
      <div className="pt-4 px-8 pb-8 mx-auto report-container">
        {/* Single watermark with CSS print magic */}
        <div className="watermark-overlay">
          <img src="/stb-logo-hd.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>

        {/* Header with STB Information */}
        <div className="mb-8 border-b pb-6">
          {/* Refresh suggestion banner */}
          {showRefreshSuggestion && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3 no-print">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <RefreshCw className="h-4 w-4 text-blue-600 mr-2" />
                  <span className="text-sm text-blue-800">
                    Es posible que comentarios de fotos y otros cambios recientes no se reflejen. ¿Desea actualizar?
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => fetchOrderData(true)}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Actualizar
                  </button>
                  <button
                    onClick={() => setShowRefreshSuggestion(false)}
                    className="px-3 py-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Usar datos actuales
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-start">
            {/* STB Logo (Left) */}
            <div className="flex items-start justify-center">
              <img 
                src="/stb-logo.gif" 
                alt="Servicios Técnicos de Bombeo" 
                className="h-24 w-auto object-contain"
              />
            </div>
            
            {/* Refresh Button (Center) */}
            <div className="no-print flex items-center">
              <button
                onClick={() => fetchOrderData(true)}
                disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Actualizar datos del reporte"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            {/* Company Information (Right) */}
            <div className="text-right">
              <h1 className="text-xl font-bold text-gray-900 mb-0.5">Servicios Técnicos de Bombeo</h1>
              <div className="text-sm text-gray-700 space-y-0.5">
                <p className="font-medium">Taller de Servicio</p>
                <p>Guadalupe Victoria #73 Col. San Benito</p>
                <p>Hermosillo, Sonora, México, C.P. 83190</p>
                <p className="flex items-center justify-end">
                  Teléfono: 
                  <svg className="h-4 w-4 mx-1" viewBox="0 0 24 24" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.251"/>
                  </svg>
                  (662)2153758
                </p>
              </div>
            </div>
          </div>
          
          {/* Report Title (Centered below) */}
          <div className="text-center mt-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {order?.tipo_de_orden === 'Garantía' 
                ? 'SOLICITUD DE GARANTÍA'
                : order?.tipo_de_orden === 'Reparación'
                ? 'REPORTE DE SERVICIO'
                : `REPORTE DE ${order?.tipo_de_orden?.toUpperCase() || 'ORDEN'}`
              }
            </h2>
          </div>
        </div>

        {/* Order Information */}
        <div className="mb-6 info-block">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-700">Folio:</span>
                <span className="ml-1 font-bold text-gray-900">{order?.folio}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Recepción de equipo:</span>
                <span className="ml-1 text-gray-900">{formatDate(order?.fecha)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Reporte:</span>
                <span className="ml-1 text-gray-900">{formatDate(new Date())}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Client Information with Temporary Editing */}
        {order?.tipo_de_orden !== 'Fabricación' && tempClientData && (
          <div className="mb-6 major-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center section-header">
                <Building className="h-5 w-5 text-green-600 mr-2" />
                Información del cliente
              </h3>
              <div className="no-print flex space-x-2">
                {isEditingClient ? (
                  <>
                    <button
                      onClick={handleClientSave}
                      className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded"
                      title="Guardar cambios temporales"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleClientCancel}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded"
                      title="Cancelar edición"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleClientEdit}
                    className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded"
                    title="Editar información temporalmente"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg section-content info-block">
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {(() => {
                  const principalContact = getPrincipalContact(tempClientData.contactos);
                  const clientDisplayName = getClientDisplayName(tempClientData);
                  const contactName = principalContact?.nombre;
                  const showSeparateContact = contactName && contactName !== clientDisplayName;

                  return (
                    <>
                      <div>
                        {isEditingClient ? (
                          <input
                            type="text"
                            value={tempClientData.nombreEmpresa || ''}
                            onChange={(e) => handleClientDataChange('nombreEmpresa', e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-gray-900 font-medium">
                            {clientDisplayName}
                          </span>
                        )}
                      </div>

                      {showSeparateContact && (
                        <div>
                          <span className="font-medium text-gray-700">Contacto:</span>
                          {isEditingClient ? (
                            <input
                              type="text"
                              value={contactName || ''}
                              onChange={(e) => handleContactDataChange(0, 'nombre', e.target.value)}
                              className="ml-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          ) : (
                            <span className="ml-1 text-gray-900">{contactName}</span>
                          )}
                        </div>
                      )}
                      
                      {principalContact && (
                        <div>
                          <span className="font-medium text-gray-700">Teléfono:</span>
                          {isEditingClient ? (
                            <input
                              type="text"
                              value={principalContact.telefono || ''}
                              onChange={(e) => handleContactDataChange(0, 'telefono', e.target.value)}
                              className="ml-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          ) : (
                            <span className="ml-1 text-gray-900">{principalContact.telefono}</span>
                          )}
                        </div>
                      )}

                      {principalContact && (principalContact.email || isEditingClient) && (
                        <div>
                          <span className="font-medium text-gray-700">Email:</span>
                          {isEditingClient ? (
                            <input
                              type="email"
                              value={principalContact.email || ''}
                              onChange={(e) => handleContactDataChange(0, 'email', e.target.value)}
                              className="ml-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          ) : (
                            <span className="ml-1 text-gray-900">{principalContact.email}</span>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Equipment Information with Photos */}
        {(() => {
          // Filter valid equipment before checking length
          const validEquipment = equipment.filter(equipo => {
            if (!equipo) return false;
            if (equipo.type === 'catalog' && !equipo.catalogData) return false;
            if (equipo.type === 'exempt' && !equipo.descripcion) return false;
            return true;
          });
          
          console.log('Equipment debug:', { 
            totalEquipment: equipment.length, 
            validEquipment: validEquipment.length, 
            equipment: equipment,
            validEquipment: validEquipment 
          });
          
          return validEquipment.length > 0 ? (
          <div className="mb-8 major-section">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center section-header">
              <Package className="h-5 w-5 text-purple-600 mr-2" />
              Equipos
            </h3>
            
            {validEquipment.map((equipo, equipmentIndex) => {
              // Determine if this equipment has many photos (would be large content)
              const hasPhotos = equipo.photos && equipo.photos.filter(photo => 
                !photo.downloadURL.toLowerCase().includes('.webm')
              ).length > 0;
              const hasLargeContent = hasPhotos || (equipo.catalogData && Object.keys(getMergedSpecifications(equipo)).length > 5);
              
              return (
              <div key={equipmentIndex} className={`mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50 ${hasLargeContent ? 'equipment-item large-content' : 'equipment-item'}`}>
                <div className="flex items-center space-x-2 mb-3 no-print">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    equipo.type === 'catalog' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                    {equipo.type === 'catalog' ? 'Catálogo' : 'Exento'}
                  </span>
                </div>

                {equipo.type === 'catalog' && equipo.catalogData ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 equipment-grid">
                    {/* Left Column - Basic Equipment Info */}
                    <div className="space-y-2 equipment-basic-info">
                      <h4 className="font-medium text-gray-900">
                        {equipo.catalogData.tipo && `${equipo.catalogData.tipo} `}
                        {equipo.catalogData.marca}
                        {' '}{equipo.catalogData.modelo}
                        {equipo.catalogData.numeroParte && ` (${equipo.catalogData.numeroParte})`}
                      </h4>
                      {equipo.series && (
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">Serie:</span> {equipo.series}
                        </div>
                      )}
                      {equipo.notes && (
                        <div className="text-sm">
                          <span className="font-medium text-gray-600">Notas:</span>
                          <span className="text-gray-900 ml-1">{equipo.notes}</span>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Specifications */}
                    <div>
                      {(() => {
                        const mergedSpecs = getMergedSpecifications(equipo);
                        if (Object.keys(mergedSpecs).length === 0) return null;

                        const orderedSpecs = getOrderedSpecifications(mergedSpecs);
                        const useMultiColumn = orderedSpecs.length > 5;

                        return (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Especificaciones</h5>
                            <ul className={`text-sm text-gray-900 space-y-1 ${useMultiColumn ? 'specs-multi-column' : ''}`}>
                              {orderedSpecs.map(([specName, specValue]) => (
                                <li key={specName} className="flex">
                                  <span className="mr-2">•</span>
                                  <span>
                                    <strong>{specName}:</strong> {specValue}
                                    {isCustomSpec(equipo, specName, specValue) && (
                                      <span className="no-print ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                        Personalizado
                                      </span>
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 equipment-basic-info">
                    <h4 className="font-medium text-gray-900">
                      {equipo.descripcion || 'Equipo sin descripción'}
                    </h4>
                    {equipo.series && (
                      <div className="text-sm">
                        <span className="font-medium text-gray-600">Serie:</span>
                        <span className="text-gray-900 ml-1">{equipo.series}</span>
                      </div>
                    )}
                    {equipo.notes && (
                      <div className="text-sm">
                        <span className="font-medium text-gray-600">Notas:</span>
                        <span className="text-gray-900 ml-1">{equipo.notes}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Equipment Photos with Visibility Control */}
                {(() => {
                  // Filter out webm files first
                  const filteredPhotos = equipo.photos ? equipo.photos.filter(photo => 
                    !photo.downloadURL.toLowerCase().includes('.webm')
                  ) : [];
                  
                  if (filteredPhotos.length === 0) return null;
                  
                  const useCompactLayout = shouldUseCompactLayout(equipo, equipmentIndex, validEquipment);
                  
                  return (
                    <div className={`photos-section ${useCompactLayout ? 'compact-layout' : ''}`}>
                      <div className="flex items-center justify-between mb-3 photos-header">
                        <h5 className="text-sm font-medium text-gray-700">Fotografías</h5>
                        <div className="no-print text-xs text-gray-500">
                          Haz clic en el ícono de ojo para ocultar/mostrar fotos en el reporte
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 photo-grid">
                        {filteredPhotos.map((photo, photoIndex) => {
                        const isVisible = isPhotoVisible(equipmentIndex, photo.downloadURL);
                        return (
                          <div key={photo.downloadURL} className={`relative photo-item ${isVisible ? '' : 'opacity-30 no-print'}`}>
                            {/* Photo Visibility Toggle (Hidden on Print) */}
                            <button
                              onClick={() => togglePhotoVisibility(equipmentIndex, photo.downloadURL)}
                              className="no-print absolute top-2 right-2 z-10 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                              title={isVisible ? 'Ocultar en reporte' : 'Mostrar en reporte'}
                            >
                              {isVisible ? (
                                <Eye className="h-4 w-4 text-green-600" />
                              ) : (
                                <EyeOff className="h-4 w-4 text-red-600" />
                              )}
                            </button>

                            {/* Photo (only shown if visible) */}
                            {isVisible && (
                              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                <img
                                  src={photo.downloadURL}
                                  alt={`Foto ${photoIndex + 1} del equipo`}
                                  className="w-full h-48 object-cover"
                                />
                                {photo.comment && (
                                  <div className="p-2 bg-gray-50 border-t">
                                    <p className="text-xs text-gray-700">{photo.comment}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
              );
            })}
          </div>
          ) : null;
        })()}

        {/* Customer Comments - separate section */}
        {order?.comentarios_cliente && order.comentarios_cliente.trim() && (
          <div className="mb-8 major-section">
            <div className="bg-gray-50 p-4 rounded-lg section-content">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comentarios del Cliente
                </label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{order.comentarios_cliente}</p>
              </div>
            </div>
          </div>
        )}

        {/* Revision Results - with header only when revision results exist */}
        {order?.resultados_revision && order.resultados_revision.trim() && (
          <div className="mb-8 major-section">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 section-header">Resultados de la revisión</h3>
            <div className="bg-gray-50 p-4 rounded-lg section-content">
              <div>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{order.resultados_revision}</p>
              </div>
            </div>
          </div>
        )}

        {/* Warranty Information for Garantía orders */}
        {order?.tipo_de_orden === 'Garantía' && (order?.rma || order?.folio_compra || order?.fecha_compra_equipo || order?.veredicto || order?.detalle_veredicto) && (
          <div className={`mb-8 major-section ${(order?.detalle_veredicto && order.detalle_veredicto.length > 300) ? 'warranty-section large-content' : 'warranty-section'}`}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 section-header">Información sobre Garantía</h3>
            <div className="bg-gray-50 p-4 rounded-lg space-y-4 section-content">
              {order.rma && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Información de RMA
                  </label>
                  <p className="text-sm text-gray-900">{order.rma}</p>
                </div>
              )}
              {order.folio_compra && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Folio de Compra
                  </label>
                  <p className="text-sm text-gray-900">{order.folio_compra}</p>
                </div>
              )}
              {order.fecha_compra_equipo && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Compra del Equipo
                  </label>
                  <p className="text-sm text-gray-900">{formatDate(order.fecha_compra_equipo)}</p>
                </div>
              )}
              {order.veredicto && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Veredicto
                  </label>
                  <p className="text-sm text-gray-900">{order.veredicto}</p>
                </div>
              )}
              {order.detalle_veredicto && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Detalle del Veredicto
                  </label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{order.detalle_veredicto}</p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default OrderReport;